import { authenticationService } from "@authentication/services/AuthenticationService";

// Simple AuditAPI following the same pattern as weededDocumentsApi
export const auditApi = {
  /**
   * Test API connection
   * @returns {Promise<boolean>} - Connection status
   */
  testConnection: async () => {
    try {
      await authenticationService._makeRequest("get", "/audit/logs");
      return true;
    } catch (error) {
      console.error("[AuditAPI] Connection test failed:", error);
      return false;
    }
  },

  /**
   * Get all audit logs
   * @returns {Promise<Object>} - List of audit logs with pagination info
   */
  getAllLogs: async () => {
    const response = await authenticationService._makeRequest(
      "get",
      "/audit/logs"
    );
    return response.data;
  },

  /**
   * Create new audit log
   * @param {Object} logData - Audit log data
   * @param {string} logData.action - Action performed (required)
   * @param {Object|string} logData.details - Log details (required)
   * @param {string} [logData.created_by] - User who performed the action (optional)
   * @returns {Promise<Object>} - Created audit log
   */
  createLog: async (logData) => {
    // Validate required fields
    if (!logData.action || typeof logData.action !== "string") {
      throw new Error("Action is required and must be a string");
    }

    if (!logData.details) {
      throw new Error("Details are required");
    }

    // Build payload with only expected fields
    const payload = {
      action: logData.action.trim(),
      details: logData.details,
    };

    // Add created_by if provided
    if (logData.created_by && typeof logData.created_by === "string") {
      payload.created_by = logData.created_by.trim();
    }

    const response = await authenticationService._makeRequest(
      "post",
      "/audit/logs",
      payload
    );
    return response.data;
  },

  /**
   * Get audit log by ID
   * @param {string} logId - Log ID (UUID)
   * @returns {Promise<Object>} - Audit log details
   */
  getLogById: async (logId) => {
    if (!logId) {
      throw new Error("Log ID is required");
    }

    const response = await authenticationService._makeRequest(
      "get",
      `/audit/logs/${logId}`
    );
    return response.data;
  },

  /**
   * Update audit log
   * @param {string} logId - Log ID (UUID)
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} - Updated audit log
   */
  updateLog: async (logId, updateData) => {
    if (!logId) {
      throw new Error("Log ID is required");
    }

    if (!updateData || Object.keys(updateData).length === 0) {
      throw new Error("Update data is required");
    }

    const response = await authenticationService._makeRequest(
      "put",
      `/audit/logs/${logId}`,
      updateData
    );
    return response.data;
  },

  /**
   * Delete audit log
   * @param {string} logId - Log ID (UUID)
   * @returns {Promise<Object>} - Deletion confirmation
   */
  deleteLog: async (logId) => {
    if (!logId) {
      throw new Error("Log ID is required");
    }

    const response = await authenticationService._makeRequest(
      "delete",
      `/audit/logs/${logId}`
    );
    return response.data;
  },
};

// Enhanced AuditService with better error handling and audit-specific functionality
export const AuditService = {
  // Use the simplified API
  api: auditApi,

  // Common audit action types
  ACTION_TYPES: {
    CREATE: "CREATE",
    UPDATE: "UPDATE",
    DELETE: "DELETE",
    LOGIN: "LOGIN",
    LOGOUT: "LOGOUT",
    VIEW: "VIEW",
    DOWNLOAD: "DOWNLOAD",
    UPLOAD: "UPLOAD",
    APPROVE: "APPROVE",
    REJECT: "REJECT",
    EXPORT: "EXPORT",
    IMPORT: "IMPORT",
    CONFIGURE: "CONFIGURE",
    BACKUP: "BACKUP",
    RESTORE: "RESTORE",
    PERMISSION_CHANGE: "PERMISSION_CHANGE",
    STATUS_CHANGE: "STATUS_CHANGE",
  },

  // Log severity levels
  SEVERITY_LEVELS: {
    INFO: "info",
    WARNING: "warning",
    ERROR: "error",
    CRITICAL: "critical",
  },

  /**
   * Test API connection
   * @returns {Promise<boolean>} - Connection status
   */
  async testConnection() {
    return await this.api.testConnection();
  },

  /**
   * Create audit log entry with validation and retry logic
   * @param {string} action - Action performed
   * @param {Object|string} details - Log details
   * @param {Object} options - Log options
   * @param {string} [options.userId] - User ID who performed the action
   * @param {string} [options.severity] - Log severity level
   * @param {number} [options.retries] - Number of retry attempts
   * @returns {Promise<Object>} - Created log entry
   */
  async createAuditLog(action, details, options = {}) {
    const {
      userId = null,
      severity = this.SEVERITY_LEVELS.INFO,
      retries = 1,
    } = options;

    // Input validation
    if (!action || typeof action !== "string") {
      throw new Error("Action is required and must be a string");
    }

    const trimmedAction = action.trim();
    if (trimmedAction.length === 0) {
      throw new Error("Action cannot be empty");
    }

    if (!details) {
      throw new Error("Details are required");
    }

    // Enhance details with additional metadata
    const enhancedDetails =
      typeof details === "string" ? { message: details } : { ...details };

    // Add metadata
    enhancedDetails.timestamp = new Date().toISOString();
    if (severity) {
      enhancedDetails.severity = severity;
    }

    // Add browser/client info if available
    if (typeof window !== "undefined") {
      enhancedDetails.client_info = {
        user_agent: navigator.userAgent,
        url: window.location.href,
        referrer: document.referrer,
      };
    }

    // Build log data
    const logData = {
      action: trimmedAction,
      details: enhancedDetails,
    };

    if (userId && typeof userId === "string") {
      logData.created_by = userId.trim();
    }

    // Retry logic with exponential backoff
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await this.api.createLog(logData);
      } catch (error) {
        if (attempt === retries) {
          throw error;
        }

        // Don't retry on validation errors (4xx except 429)
        if (
          error.response?.status >= 400 &&
          error.response?.status < 500 &&
          error.response?.status !== 429
        ) {
          throw error;
        }

        // Exponential backoff: wait 1s, 2s, 4s, etc.
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  },

  /**
   * Get audit logs with filtering and pagination
   * @param {Object} options - Query options
   * @param {string} [options.action] - Filter by action
   * @param {string} [options.userId] - Filter by user
   * @param {Date} [options.startDate] - Filter from date
   * @param {Date} [options.endDate] - Filter to date
   * @returns {Promise<Array>} - List of audit logs
   */
  async getAuditLogs(options = {}) {
    try {
      const response = await this.api.getAllLogs();
      let logs = Array.isArray(response) ? response : response.data || [];

      // Apply client-side filtering if options provided
      if (options.action) {
        logs = logs.filter(
          (log) =>
            log.action &&
            log.action.toLowerCase().includes(options.action.toLowerCase())
        );
      }

      if (options.userId) {
        logs = logs.filter(
          (log) =>
            log.created_by &&
            log.created_by.toLowerCase().includes(options.userId.toLowerCase())
        );
      }

      if (options.startDate) {
        const startTime = new Date(options.startDate).getTime();
        logs = logs.filter(
          (log) =>
            log.created_at && new Date(log.created_at).getTime() >= startTime
        );
      }

      if (options.endDate) {
        const endTime = new Date(options.endDate).getTime();
        logs = logs.filter(
          (log) =>
            log.created_at && new Date(log.created_at).getTime() <= endTime
        );
      }

      return logs;
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      throw error;
    }
  },

  /**
   * Get audit log by ID with error handling
   * @param {string} logId - Log ID (UUID)
   * @returns {Promise<Object|null>} - Log details or null if not found
   */
  async getLogById(logId) {
    try {
      const response = await this.api.getLogById(logId);
      return response;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Update audit log with validation
   * @param {string} logId - Log ID (UUID)
   * @param {Object} updateData - Update data
   * @param {string} [updateData.action] - Updated action
   * @param {Object|string} [updateData.details] - Updated details
   * @returns {Promise<Object>} - Updated log
   */
  async updateAuditLog(logId, updateData) {
    if (!updateData || Object.keys(updateData).length === 0) {
      throw new Error("Update data is required");
    }

    const payload = {};

    if (updateData.action) {
      if (
        typeof updateData.action !== "string" ||
        updateData.action.trim().length === 0
      ) {
        throw new Error("Action must be a non-empty string");
      }
      payload.action = updateData.action.trim();
    }

    if (updateData.details !== undefined) {
      payload.details = updateData.details;
    }

    return await this.api.updateLog(logId, payload);
  },

  /**
   * Delete audit log with confirmation
   * @param {string} logId - Log ID (UUID)
   * @param {boolean} confirm - Whether to skip confirmation
   * @returns {Promise<boolean>} - Deletion success
   */
  async deleteLogWithConfirmation(logId, confirm = false) {
    if (
      !confirm &&
      typeof window !== "undefined" &&
      !window.confirm(
        "Are you sure you want to delete this audit log? This action cannot be undone."
      )
    ) {
      return false;
    }

    try {
      await this.api.deleteLog(logId);
      return true;
    } catch (error) {
      console.error("Failed to delete audit log:", error);
      throw error;
    }
  },

  /**
   * Get available action types
   * @returns {Object} - Available action types
   */
  getAvailableActionTypes() {
    return this.ACTION_TYPES;
  },

  /**
   * Get available severity levels
   * @returns {Object} - Available severity levels
   */
  getAvailableSeverityLevels() {
    return this.SEVERITY_LEVELS;
  },

  /**
   * Validate action type against available options
   * @param {string} action - Action to validate
   * @returns {boolean} - Whether action is valid
   */
  isValidActionType(action) {
    return Object.values(this.ACTION_TYPES).includes(action);
  },

  /**
   * Validate severity level
   * @param {string} severity - Severity to validate
   * @returns {boolean} - Whether severity is valid
   */
  isValidSeverityLevel(severity) {
    return Object.values(this.SEVERITY_LEVELS).includes(severity);
  },

  /**
   * Format audit log for display
   * @param {Object} log - Audit log object
   * @returns {Object} - Formatted log
   */
  formatLogForDisplay(log) {
    if (!log) return null;

    return {
      id: log.id,
      action: log.action,
      details:
        typeof log.details === "string"
          ? log.details
          : JSON.stringify(log.details, null, 2),
      createdBy: log.created_by || "System",
      createdAt: log.created_at
        ? new Date(log.created_at).toLocaleString()
        : "Unknown",
      severity: log.details?.severity || "info",
      rawData: log,
    };
  },

  /**
   * Export audit logs as CSV
   * @param {Array} logs - Logs to export
   * @returns {string} - CSV content
   */
  exportLogsAsCSV(logs) {
    if (!Array.isArray(logs) || logs.length === 0) {
      throw new Error("No logs to export");
    }

    const headers = ["ID", "Action", "Details", "Created By", "Created At"];
    const csvRows = [headers.join(",")];

    logs.forEach((log) => {
      const formatted = this.formatLogForDisplay(log);
      const row = [
        formatted.id,
        `"${formatted.action}"`,
        `"${formatted.details.replace(/"/g, '""')}"`,
        `"${formatted.createdBy}"`,
        `"${formatted.createdAt}"`,
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  },

  /**
   * Quick log methods for common actions
   */
  async logUserAction(action, details, userId = null) {
    return await this.createAuditLog(action, details, {
      userId,
      severity: this.SEVERITY_LEVELS.INFO,
    });
  },

  async logSystemEvent(action, details) {
    return await this.createAuditLog(action, details, {
      severity: this.SEVERITY_LEVELS.INFO,
    });
  },

  async logError(action, error, userId = null) {
    const details = {
      error_message: error.message,
      error_stack: error.stack,
      error_type: error.constructor.name,
    };

    return await this.createAuditLog(action, details, {
      userId,
      severity: this.SEVERITY_LEVELS.ERROR,
    });
  },

  async logSecurityEvent(action, details, userId = null) {
    return await this.createAuditLog(action, details, {
      userId,
      severity: this.SEVERITY_LEVELS.CRITICAL,
    });
  },
};

export default auditApi;
